import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { subDays } from 'date-fns'

import { Modal } from '@/components/Modal'
import { BaseView } from '@/components/base'
import { AppButton } from '@/components/AppButton'
import { DatePicker } from '@/components/DatePicker'
import { DateInputField } from './DateInputField'
import { RangeDate } from '@/domain/transactions'
import useOffsets from '@/hooks/useOffsets'

interface ChoosePeriodModalProps {
  isOpen: boolean
  onClose: () => void
  appliedRangeDate: RangeDate
  setAppliedRangeDate: (rangeDate: RangeDate) => void
}

const maximumStartDate = new Date()

const defaultRangeDate = {
  startDate: subDays(maximumStartDate, 1),
  endDate: new Date(),
}

export const ChoosePeriodModal: React.FC<ChoosePeriodModalProps> = ({
  isOpen,
  onClose,
  appliedRangeDate,
  setAppliedRangeDate,
}) => {
  const { t } = useTranslation()
  const offsets = useOffsets()
  const [rangeDate, setRangeDate] = useState<RangeDate>(defaultRangeDate)
  const [focusedInput, setFocusedInput] = useState<'startDate' | 'endDate'>(
    'startDate',
  )

  const hasAppliedRangeDate =
    !!appliedRangeDate.startDate && !!appliedRangeDate.endDate

  useEffect(() => {
    setRangeDate(hasAppliedRangeDate ? appliedRangeDate : defaultRangeDate)
  }, [appliedRangeDate, hasAppliedRangeDate])

  const hasStartAndEndDate = !!rangeDate.startDate && !!rangeDate.endDate

  const hasChanges =
    rangeDate.startDate !== appliedRangeDate.startDate ||
    rangeDate.endDate !== appliedRangeDate.endDate

  const handleClose = () => {
    // Restore without applying
    setRangeDate(hasAppliedRangeDate ? appliedRangeDate : defaultRangeDate)
    setFocusedInput('startDate')
    onClose()
  }

  const handleApply = () => {
    // Updating values
    setAppliedRangeDate(rangeDate)
    setFocusedInput('startDate')
    onClose()
  }

  // Форматирование даты в формат DD.MM.YYYY
  const formatDate = (date: Date | null): string => {
    if (!date) return ''
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }

  const handleDateChange = (date: Date, inputType: 'startDate' | 'endDate') => {
    // Нормализуем даты: startDate - начало дня, endDate - конец дня
    const normalizedDate = new Date(date)
    if (inputType === 'startDate') {
      normalizedDate.setHours(0, 0, 0, 0)
    } else {
      normalizedDate.setHours(23, 59, 59, 999)
    }
    setRangeDate({ ...rangeDate, [inputType]: normalizedDate })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('translation:choosePeriod', 'Choose a period')}
      showHandle={true}
    >
      <BaseView
        gap={26}
        py={10}
        style={{
          paddingBottom: offsets.bottom,
        }}
      >
        {/* Date Inputs */}
        <BaseView row gap={12} style={{ width: '100%' }}>
          <DateInputField
            label={t('translation:startDate', 'Start Date')}
            value={formatDate(rangeDate.startDate)}
            isFocused={focusedInput === 'startDate'}
            onPress={() => setFocusedInput('startDate')}
          />
          <DateInputField
            label={t('translation:endDate', 'End Date')}
            value={formatDate(rangeDate.endDate)}
            isFocused={focusedInput === 'endDate'}
            onPress={() => setFocusedInput('endDate')}
          />
        </BaseView>

        {/* Date Picker - один для редактирования выбранной даты */}
        <BaseView gap={16}>
          <DatePicker
            value={
              focusedInput === 'startDate'
                ? rangeDate.startDate
                : rangeDate.endDate
            }
            onChange={(date) => handleDateChange(date, focusedInput)}
            minDate={
              focusedInput === 'endDate' && rangeDate.startDate
                ? rangeDate.startDate
                : undefined
            }
            maxDate={maximumStartDate}
          />
        </BaseView>

        {/* Apply Button */}
        <AppButton
          label={t('translation:applyFilter', 'Apply filter')}
          onPress={handleApply}
          disabled={!hasStartAndEndDate || !hasChanges}
          style={{
            marginTop: 8,
          }}
        />
      </BaseView>
    </Modal>
  )
}
